'use client';

import type {
  ContextBinding,
  DataSourceConfig,
  FormComponent,
} from '@commenda/types';
import { useContext, useMemo } from 'react';
import { DSLContextMarker } from '../../../constants';
import { FormContext } from '../../../hooks/FormContext';
import { AutoDetectFieldComponent } from '../AutoDetectField';

interface InlineFieldsContainerProps {
  parentFieldId: string;
  entityId: string;
  entityType: string;
  isNewEntity: boolean;
  template: FormComponent[];
  /** The context key for identifying this entity (e.g., 'corporationId'). Comes from the entity schema. */
  idContextKey?: string;
}

export const ENTITY_CONTEXT_MARKER = DSLContextMarker.ENTITY_CONTEXT;

/**
 * InlineFieldsContainer Component
 *
 * Renders AutoDetect fields from the template prop with entity-specific context.
 * This component handles:
 * - Overriding context bindings to use the specific entity's ID
 * - Generating unique field IDs per entity
 * - Handling "Add New" flow (no entity ID yet)
 */
export const InlineFieldsContainer = ({
  parentFieldId,
  entityId,
  entityType,
  isNewEntity,
  template,
  idContextKey,
}: InlineFieldsContainerProps) => {
  const { dslContext } = useContext(FormContext);

  // Build the entity-specific context
  // For existing entities, we have the ID
  // For new entities, we don't have an ID yet (will be created on submit)
  const entityContext = useMemo(() => {
    if (isNewEntity) {
      // For new entities, don't include the entity ID in context
      // The submit logic will handle creating the entity first
      return { ...dslContext };
    }

    // For existing entities, add the entity ID to context using idContextKey from schema
    const resolvedIdKey = idContextKey || `${entityType}Id`;
    const ctx: Record<string, unknown> = {
      ...dslContext,
      [resolvedIdKey]: Number(entityId),
    };
    // Key person rows: also set directorId so directorDocument fields get correct context
    if (entityType === 'keyPerson') {
      ctx.directorId = Number(entityId);
    }
    return ctx;
  }, [dslContext, entityType, entityId, isNewEntity, idContextKey]);

  // Transform template fields to have entity-specific field IDs and context bindings
  const transformedTemplate = useMemo(() => {
    return template.map((field) => {
      // Generate a unique field ID for this entity
      const entityFieldId = `${parentFieldId}-entity-${entityId}-${field.fieldId || field.id}`;

      // Transform the data source to use entity context
      let transformedDataSource: DataSourceConfig | undefined =
        field.dataSource;

      if (field.dataSource?.commendaOS) {
        const resolvedIdKey = idContextKey || `${entityType}Id`;
        // DirectorDocument fields need directorId in context; key person rows use same id
        const contextKeyForField =
          field.dataSource.commendaOS.entity === 'directorDocument'
            ? 'directorId'
            : resolvedIdKey;

        if (isNewEntity) {
          // For new entities, remove context bindings entirely
          // The fields should be enabled for data entry (creating a new entity)
          transformedDataSource = {
            ...field.dataSource,
            commendaOS: {
              ...field.dataSource.commendaOS,
              // Clear context bindings for new entities - fields should be enabled
              contextBindings: [],
            },
          };
        } else {
          // For existing entities, use the entity's ID as context (directorId for directorDocument)
          const newContextBindings: ContextBinding[] = [
            {
              contextKey: contextKeyForField,
              sourceFieldId: ENTITY_CONTEXT_MARKER, // Special marker
            },
          ];

          // Preserve any other context bindings that aren't for the entity/director ID
          const existingBindings =
            field.dataSource.commendaOS.contextBindings || [];
          const otherBindings = existingBindings.filter(
            (b) =>
              b.contextKey !== resolvedIdKey && b.contextKey !== 'directorId'
          );

          transformedDataSource = {
            ...field.dataSource,
            commendaOS: {
              ...field.dataSource.commendaOS,
              contextBindings: [...newContextBindings, ...otherBindings],
            },
          };
        }
      }

      return {
        ...field,
        fieldId: entityFieldId,
        dataSource: transformedDataSource,
        // Store metadata about the entity context for submit logic
        props: {
          ...field.props,
          __entityContext__: {
            parentFieldId,
            entityId,
            entityType,
            isNewEntity,
            originalFieldId: field.fieldId || field.id,
          },
        },
      };
    });
  }, [
    template,
    parentFieldId,
    entityId,
    entityType,
    isNewEntity,
    idContextKey,
  ]);

  if (template.length === 0) {
    return null;
  }

  return (
    <div className="border border-t-0 border-border-primary rounded-b-md p-4 bg-bg-secondary-subtle">
      <div className="space-y-4">
        {transformedTemplate.map((field) => {
          // For now, we render AutoDetectField components
          // The field type is determined by the template configuration
          if (
            field.component === 'AutoDetectField' ||
            field.dataSource?.commendaOS
          ) {
            return (
              <AutoDetectFieldWithEntityContext
                key={field.id}
                field={field}
                entityContext={entityContext}
                isNewEntity={isNewEntity}
              />
            );
          }

          // Fallback for other component types (shouldn't happen in normal use)
          return (
            <div key={field.id} className="text-sm text-text-tertiary">
              Unsupported field type: {field.component}
            </div>
          );
        })}
      </div>

      {isNewEntity && (
        <p className="mt-3 text-xs text-text-tertiary">
          Fill in the required fields to create a new {entityType}.
        </p>
      )}
    </div>
  );
};

/**
 * AutoDetectFieldWithEntityContext
 *
 * Wrapper that provides entity-specific context to AutoDetectField
 */
interface AutoDetectFieldWithEntityContextProps {
  field: FormComponent;
  entityContext: Record<string, unknown>;
  isNewEntity: boolean;
}

const AutoDetectFieldWithEntityContext = ({
  field,
  entityContext,
  isNewEntity,
}: AutoDetectFieldWithEntityContextProps) => {
  const { formData, updateField, errors, validateField } =
    useContext(FormContext);

  // Create a modified FormContext value that includes the entity context
  // This is a simplified approach - the actual context injection happens
  // through the dataSource.commendaOS.contextBindings mechanism

  return (
    <AutoDetectFieldComponent
      fieldId={field.fieldId!}
      label={field.label}
      placeholder={field.placeholder}
      guidance={field.guidance}
      description={field.description}
      validation={field.validation}
      dataSource={field.dataSource}
      props={{
        ...field.props,
        // Pass entity context as props for submit logic to use
        __entityContext__: {
          ...(field.props?.__entityContext__ as Record<string, unknown>),
          dslContext: entityContext,
          isNewEntity,
        },
      }}
    />
  );
};
